import { useEffect, useState } from 'react';

export interface CountdownLineProps {
  /** ISO timestamp when the buyback offer auto-fires. */
  autoSellAt: string | null;
}

/**
 * "23h 50m left" under a Vault row's title (partner Figma 2026-10-08: a
 * plain amber line, no chip or pie). Self-ticking: re-renders
 * exactly when the displayed minute flips (one timeout per flip, not a
 * per-second interval), and the final flip past `autoSellAt` unmounts it.
 */
export function CountdownLine({ autoSellAt }: CountdownLineProps) {
  const [tick, setTick] = useState(0);

  // Schedule the next re-render at the upcoming minute boundary of the
  // remaining time (+small epsilon so the recompute lands on the new
  // minute). Re-arms itself after every tick until expiry.
  useEffect(() => {
    if (!autoSellAt) return;
    const target = Date.parse(autoSellAt);
    if (!Number.isFinite(target)) return;
    const diffMs = target - Date.now();
    if (diffMs <= 0) return;
    const delay = (diffMs % 60_000) + 50;
    const timer = window.setTimeout(() => setTick((n) => n + 1), delay);
    return () => window.clearTimeout(timer);
  }, [autoSellAt, tick]);

  const label = countdownLabel(autoSellAt);
  if (!label) return null;
  return (
    <p className="m-0 text-[12px] leading-[16px] text-[var(--co-MyPacksItemCard-timerLineColor,#FFC933)]">
      {label}
    </p>
  );
}

function countdownLabel(autoSellAt: string | null): string | null {
  if (!autoSellAt) return null;
  const target = Date.parse(autoSellAt);
  if (!Number.isFinite(target)) return null;
  const diffMs = target - Date.now();
  if (diffMs <= 0) return null;
  return formatLabel(diffMs);
}

function formatLabel(diffMs: number): string {
  const totalMinutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  if (hours < 1) return `${Math.max(1, totalMinutes)}m left`;
  if (hours < 48) {
    const mins = totalMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}m left` : `${hours}h left`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d left`;
}
